/**
 * portfolioPills.ts — the Portfolio tab's KPI strip, as data.
 *
 * One builder for web (`renderPills`) and mobile (`Pills`) so the six
 * tiles read the same on both, and so "no blank tile" is a property the
 * test suite can assert: every pill always carries a non-empty value —
 * a metric the payload can't provide renders as an explicit "n/a"
 * (muted), never an empty string, `undefined` or `NaN`.
 */

export type PillTone = '' | 'pos' | 'neg' | 'warn' | 'muted';

export interface PortfolioPill {
  key: 'cash_flow' | 'cap_rate' | 'coc' | 'dscr' | 'ltv' | 'properties';
  label: string;
  value: string;
  tone: PillTone;
}

/** The subset of GET /portfolio/dashboard the strip reads. Every field
 *  is optional here on purpose — a partial or stale payload must still
 *  produce six complete pills. */
export interface PortfolioPillSummary {
  net_monthly_cash_flow?: number | null;
  avg_cap_rate?: number | null;
  avg_cash_on_cash?: number | null;
  portfolio_dscr?: number | null;
  ltv_ratio?: number | null;
  total_properties?: number | null;
}

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function money(n: number): string {
  return `${n < 0 ? '−' : ''}$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
}

export function buildPortfolioPills(s: PortfolioPillSummary | null | undefined): PortfolioPill[] {
  const src = s || {};
  const net = num(src.net_monthly_cash_flow);
  const cap = num(src.avg_cap_rate);
  const coc = num(src.avg_cash_on_cash);
  const dscr = num(src.portfolio_dscr);
  const ltv = num(src.ltv_ratio);
  const count = num(src.total_properties);

  return [
    {
      key: 'cash_flow',
      label: 'Projected cash flow',
      value: net == null ? 'n/a' : `${money(net)}/mo`,
      tone: net == null ? 'muted' : net >= 0 ? 'pos' : 'neg',
    },
    {
      key: 'cap_rate',
      label: 'Cap rate',
      value: cap == null ? 'n/a' : `${cap.toFixed(1)}%`,
      tone: cap == null ? 'muted' : '',
    },
    {
      key: 'coc',
      label: 'CoC return',
      value: coc == null ? 'n/a' : `${coc.toFixed(1)}%`,
      tone: coc == null ? 'muted' : coc >= 0 ? 'pos' : 'neg',
    },
    {
      // null = debt-free portfolio: DSCR is undefined, shown as muted n/a.
      key: 'dscr',
      label: 'DSCR',
      value: dscr == null ? 'n/a' : dscr.toFixed(2),
      tone: dscr == null ? 'muted' : dscr >= 1.25 ? 'pos' : dscr >= 1 ? 'warn' : 'neg',
    },
    {
      key: 'ltv',
      label: 'LTV',
      value: ltv == null ? 'n/a' : `${ltv.toFixed(0)}%`,
      tone: ltv == null ? 'muted' : '',
    },
    {
      key: 'properties',
      label: 'Properties',
      value: count == null ? '0' : String(Math.round(count)),
      tone: '',
    },
  ];
}
